/**
 * File: assets/basari-eye-test-date-time.js
 * Purpose: Eye Test Date & Time Selection UI — mock AvailabilityService + presentation.
 * Sprint: 1B.16C — Eye Test Date & Time Selection UI
 * Owner: Basari Theme / PDP
 * Dependencies: basari-eye-test-booking.js (EyeTestController API), drawer host mount
 *
 * Rules:
 *   - Never write directly to lensFlow.eyeTestBooking
 *   - Date / time / month go through EyeTestController.selectDate|selectTime|selectMonth
 *   - Navigation via EyeTestController.requestNavigation / requestBack
 *   - Close reuses drawer closeDrawer / onClose
 *   - No backend / Google Calendar / booking / ICS
 */
(function (global) {
  'use strict';

  var BasariEyeTestDateTime = global.BasariEyeTestDateTime || {};
  var READY_ATTR = 'data-basari-eye-test-date-time-ready';
  var SELECTED_CLASS = 'is-selected';
  var DISABLED_CLASS = 'is-disabled';
  var OUTSIDE_CLASS = 'is-outside';
  var PAST_CLASS = 'is-past';
  var TODAY_CLASS = 'is-today';
  var ROUTE_CONFIRMATION = 'eye-test-confirmation';

  var PERIODS = ['morning', 'afternoon', 'evening'];

  /* ---------------------------------------------------------------------------
   * Temporary mock AvailabilityService (client-side only — no fetch / AJAX / API)
   * ------------------------------------------------------------------------- */

  /**
   * Static slot catalog matching design reference (Morning / Afternoon / Evening).
   * Availability flags are applied per date via a deterministic pattern.
   */
  var MOCK_SLOT_CATALOG = {
    morning: [
      { time: '09:00', available: true },
      { time: '09:30', available: true },
      { time: '10:00', available: true },
      { time: '11:00', available: false }
    ],
    afternoon: [
      { time: '13:00', available: true },
      { time: '14:30', available: true },
      { time: '15:00', available: true },
      { time: '15:30', available: true },
      { time: '16:00', available: true }
    ],
    evening: [
      { time: '17:00', available: true },
      { time: '17:30', available: true },
      { time: '18:30', available: true }
    ]
  };

  function pad2(value) {
    var n = Number(value);
    return n < 10 ? '0' + n : String(n);
  }

  function toISODate(year, month, day) {
    return String(year) + '-' + pad2(month) + '-' + pad2(day);
  }

  function parseISODate(iso) {
    var raw = String(iso || '');
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    if (!match) return null;
    var year = Number(match[1]);
    var month = Number(match[2]);
    var day = Number(match[3]);
    if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
    return { year: year, month: month, day: day };
  }

  function startOfTodayLocal() {
    var now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  function daysInMonth(year, month) {
    return new Date(year, month, 0).getDate();
  }

  /**
   * Monday-based weekday index: Mon=0 … Sun=6
   */
  function mondayIndex(year, month, day) {
    var dow = new Date(year, month - 1, day).getDay();
    return dow === 0 ? 6 : dow - 1;
  }

  function cloneSlot(slot) {
    return {
      time: slot.time,
      available: slot.available !== false
    };
  }

  function cloneSlotsByPeriod(source) {
    var out = {};
    var i;
    var period;
    for (i = 0; i < PERIODS.length; i += 1) {
      period = PERIODS[i];
      out[period] = (source[period] || []).map(cloneSlot);
    }
    return out;
  }

  /**
   * Deterministic mock: weekdays with day%3 !== 0 are bookable (excl. past).
   * Sundays closed. Saturdays open with reduced evening.
   */
  function isMockDateAvailable(year, month, day) {
    var date = new Date(year, month - 1, day);
    var today = startOfTodayLocal();
    if (date < today) return false;
    var dow = date.getDay();
    if (dow === 0) return false;
    if (day % 3 === 0) return false;
    return true;
  }

  function buildMockSlotsForDate(isoDate) {
    var parsed = parseISODate(isoDate);
    if (!parsed || !isMockDateAvailable(parsed.year, parsed.month, parsed.day)) {
      return cloneSlotsByPeriod({ morning: [], afternoon: [], evening: [] });
    }

    var slots = cloneSlotsByPeriod(MOCK_SLOT_CATALOG);
    var dow = new Date(parsed.year, parsed.month - 1, parsed.day).getDay();

    /* Design reference: 11:00 disabled; occasionally disable an afternoon slot. */
    if (parsed.day % 2 === 0 && slots.afternoon[1]) {
      slots.afternoon[1].available = false;
    }

    if (dow === 6) {
      slots.evening = slots.evening.slice(0, 1);
    }

    return slots;
  }

  function AvailabilityService(options) {
    options = options || {};
    this._catalog = options.catalog || MOCK_SLOT_CATALOG;
  }

  /**
   * @param {number} year
   * @param {number} month - 1–12
   * @returns {{ year: number, month: number, availableDates: string[] }}
   */
  AvailabilityService.prototype.getAvailableDates = function (year, month) {
    var y = Number(year);
    var m = Number(month);
    var availableDates = [];
    var total;
    var day;
    var iso;

    if (!y || m < 1 || m > 12) {
      return { year: y || null, month: m || null, availableDates: [] };
    }

    total = daysInMonth(y, m);
    for (day = 1; day <= total; day += 1) {
      if (isMockDateAvailable(y, m, day)) {
        iso = toISODate(y, m, day);
        availableDates.push(iso);
      }
    }

    return {
      year: y,
      month: m,
      availableDates: availableDates
    };
  };

  /**
   * @param {string} isoDate - YYYY-MM-DD
   * @returns {{ date: string, morning: Array, afternoon: Array, evening: Array }}
   */
  AvailabilityService.prototype.getAvailableSlots = function (isoDate) {
    var date = String(isoDate || '').trim();
    var slots = buildMockSlotsForDate(date);
    return {
      date: date,
      morning: slots.morning,
      afternoon: slots.afternoon,
      evening: slots.evening
    };
  };

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) {
      el.setAttribute('hidden', '');
    } else {
      el.removeAttribute('hidden');
    }
  }

  function formatMonthTitle(year, month, labels) {
    labels = labels || {};
    try {
      var formatter = new Intl.DateTimeFormat(undefined, {
        month: 'long',
        year: 'numeric'
      });
      return formatter.format(new Date(year, month - 1, 1));
    } catch (error) {
      var names = labels.monthNames || [];
      var name = names[month - 1] || String(month);
      return name + ' ' + year;
    }
  }

  /**
   * @param {HTMLElement} root - [data-basari-lens-eye-test-date-time-root]
   * @param {Object} options
   * @param {Function} options.getController
   * @param {Function} [options.onClose]
   * @param {AvailabilityService} [options.availabilityService]
   * @param {Object} [options.labels]
   */
  function EyeTestDateTimeUI(root, options) {
    this.root = root;
    this.options = options || {};
    this.availabilityService =
      this.options.availabilityService instanceof AvailabilityService
        ? this.options.availabilityService
        : new AvailabilityService();
    this.labels = this.options.labels || {};
    this.cache = {};
    this.bound = false;
    this.viewMonth = null;
    this.viewYear = null;
    this.selectedDate = null;
    this.selectedTime = null;
    this.availableDateSet = {};
    this._focusedDate = null;
    this._onRootEvent = this.onRootEvent.bind(this);
  }

  EyeTestDateTimeUI.prototype.getController = function () {
    if (typeof this.options.getController === 'function') {
      return this.options.getController();
    }
    return null;
  };

  EyeTestDateTimeUI.prototype.cacheSelectors = function () {
    this.cache.monthTitle = this.root.querySelector('[data-basari-eye-test-month-title]');
    this.cache.prevBtn = this.root.querySelector('[data-basari-eye-test-month-prev]');
    this.cache.nextBtn = this.root.querySelector('[data-basari-eye-test-month-next]');
    this.cache.grid = this.root.querySelector('[data-basari-eye-test-calendar-grid]');
    this.cache.dayTemplate = this.root.querySelector('[data-basari-eye-test-day-template]');
    this.cache.slotTemplate = this.root.querySelector('[data-basari-eye-test-slot-template]');
    this.cache.slotsEmpty = this.root.querySelector('[data-basari-eye-test-slots-empty]');
    this.cache.slotGroups = this.root.querySelector('[data-basari-eye-test-slot-groups]');
    this.cache.slotLists = {
      morning: this.root.querySelector('[data-basari-eye-test-slot-list="morning"]'),
      afternoon: this.root.querySelector('[data-basari-eye-test-slot-list="afternoon"]'),
      evening: this.root.querySelector('[data-basari-eye-test-slot-list="evening"]')
    };
    this.cache.confirmBtn = this.root.querySelector('[data-basari-eye-test-date-time-confirm]');
    this.cache.closeBtn = this.root.querySelector('[data-basari-eye-test-date-time-close]');
    this.cache.a11y = this.root.querySelector('[data-basari-lens-eye-test-a11y]');
  };

  EyeTestDateTimeUI.prototype.bind = function () {
    if (this.bound || !this.root) return;
    this.root.addEventListener('click', this._onRootEvent);
    this.root.addEventListener('keydown', this._onRootEvent);
    this.bound = true;
  };

  EyeTestDateTimeUI.prototype.unbind = function () {
    if (!this.bound || !this.root) return;
    this.root.removeEventListener('click', this._onRootEvent);
    this.root.removeEventListener('keydown', this._onRootEvent);
    this.bound = false;
  };

  EyeTestDateTimeUI.prototype.onRootEvent = function (event) {
    var target = event.target;
    if (!target || !this.root.contains(target)) return;

    var dayBtn = target.closest ? target.closest('[data-basari-eye-test-day]') : null;
    var slotBtn = target.closest ? target.closest('[data-basari-eye-test-slot]') : null;

    if (event.type === 'keydown') {
      if (dayBtn) {
        this.onDayKeydown(event, dayBtn);
        return;
      }
      if (slotBtn) {
        this.onSlotKeydown(event, slotBtn);
        return;
      }
      return;
    }

    if (event.type !== 'click') return;

    if (target.closest && target.closest('[data-basari-eye-test-month-prev]')) {
      this.shiftMonth(-1);
      return;
    }

    if (target.closest && target.closest('[data-basari-eye-test-month-next]')) {
      this.shiftMonth(1);
      return;
    }

    if (dayBtn) {
      if (dayBtn.disabled || dayBtn.getAttribute('aria-disabled') === 'true') return;
      this.selectDate(dayBtn.getAttribute('data-date'));
      return;
    }

    if (slotBtn) {
      if (slotBtn.disabled || slotBtn.getAttribute('aria-disabled') === 'true') return;
      this.selectTime(slotBtn.getAttribute('data-time'));
      return;
    }

    if (target.closest && target.closest('[data-basari-eye-test-date-time-confirm]')) {
      this.handleConfirm();
      return;
    }

    if (target.closest && target.closest('[data-basari-eye-test-date-time-close]')) {
      this.handleClose();
    }
  };

  EyeTestDateTimeUI.prototype.onDayKeydown = function (event, dayBtn) {
    var key = event.key;
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      if (!dayBtn.disabled && dayBtn.getAttribute('aria-disabled') !== 'true') {
        this.selectDate(dayBtn.getAttribute('data-date'));
      }
      return;
    }

    if (
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'ArrowUp' ||
      key === 'ArrowDown' ||
      key === 'Home' ||
      key === 'End'
    ) {
      event.preventDefault();
      this.moveDayFocus(dayBtn, key);
    }
  };

  EyeTestDateTimeUI.prototype.onSlotKeydown = function (event, slotBtn) {
    var key = event.key;
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      if (!slotBtn.disabled && slotBtn.getAttribute('aria-disabled') !== 'true') {
        this.selectTime(slotBtn.getAttribute('data-time'));
      }
      return;
    }

    if (key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight') {
      event.preventDefault();
      this.moveSlotFocus(slotBtn, key);
    }
  };

  EyeTestDateTimeUI.prototype.getDayButtons = function () {
    if (!this.cache.grid) return [];
    return Array.prototype.slice.call(
      this.cache.grid.querySelectorAll('[data-basari-eye-test-day]:not([disabled])')
    );
  };

  EyeTestDateTimeUI.prototype.getSlotButtons = function () {
    return Array.prototype.slice.call(
      this.root.querySelectorAll(
        '[data-basari-eye-test-slot]:not([disabled]):not([aria-disabled="true"])'
      )
    );
  };

  EyeTestDateTimeUI.prototype.moveDayFocus = function (current, key) {
    var buttons = this.getDayButtons();
    if (!buttons.length) return;

    var index = buttons.indexOf(current);
    if (index < 0) index = 0;

    var nextIndex = index;
    if (key === 'ArrowLeft') nextIndex = Math.max(0, index - 1);
    if (key === 'ArrowRight') nextIndex = Math.min(buttons.length - 1, index + 1);
    if (key === 'ArrowUp') nextIndex = Math.max(0, index - 7);
    if (key === 'ArrowDown') nextIndex = Math.min(buttons.length - 1, index + 7);
    if (key === 'Home') nextIndex = 0;
    if (key === 'End') nextIndex = buttons.length - 1;

    var next = buttons[nextIndex];
    if (!next) return;

    this.setDayTabStops(next.getAttribute('data-date'));
    next.focus();
  };

  EyeTestDateTimeUI.prototype.moveSlotFocus = function (current, key) {
    var buttons = this.getSlotButtons();
    if (!buttons.length) return;

    var index = buttons.indexOf(current);
    if (index < 0) index = 0;

    var nextIndex = index;
    if (key === 'ArrowLeft' || key === 'ArrowUp') {
      nextIndex = Math.max(0, index - 1);
    } else {
      nextIndex = Math.min(buttons.length - 1, index + 1);
    }

    var next = buttons[nextIndex];
    if (!next) return;

    this.setSlotTabStops(next.getAttribute('data-time'));
    next.focus();
  };

  EyeTestDateTimeUI.prototype.setDayTabStops = function (focusDate) {
    var buttons = this.cache.grid
      ? Array.prototype.slice.call(this.cache.grid.querySelectorAll('[data-basari-eye-test-day]'))
      : [];
    var target = focusDate || this.selectedDate || this._focusedDate;
    var i;
    var btn;
    var matched = false;

    for (i = 0; i < buttons.length; i += 1) {
      btn = buttons[i];
      if (btn.disabled) {
        btn.setAttribute('tabindex', '-1');
        continue;
      }
      if (!matched && target && btn.getAttribute('data-date') === target) {
        btn.setAttribute('tabindex', '0');
        matched = true;
      } else {
        btn.setAttribute('tabindex', '-1');
      }
    }

    if (!matched) {
      for (i = 0; i < buttons.length; i += 1) {
        if (!buttons[i].disabled) {
          buttons[i].setAttribute('tabindex', '0');
          break;
        }
      }
    }
  };

  EyeTestDateTimeUI.prototype.readState = function () {
    var controller = this.getController();
    if (!controller || typeof controller.getState !== 'function') return null;
    return controller.getState();
  };

  EyeTestDateTimeUI.prototype.hydrateFromState = function () {
    var state = this.readState();
    var today = startOfTodayLocal();
    var parsed;

    this.selectedDate = state && state.selectedDate ? String(state.selectedDate) : null;
    this.selectedTime = state && state.selectedTime ? String(state.selectedTime) : null;

    if (state && state.selectedMonth && state.selectedYear) {
      this.viewMonth = Number(state.selectedMonth);
      this.viewYear = Number(state.selectedYear);
    } else if (this.selectedDate) {
      parsed = parseISODate(this.selectedDate);
      if (parsed) {
        this.viewMonth = parsed.month;
        this.viewYear = parsed.year;
      }
    }

    if (!this.viewMonth || !this.viewYear) {
      this.viewMonth = today.getMonth() + 1;
      this.viewYear = today.getFullYear();
    }

    this._focusedDate = this.selectedDate;
  };

  EyeTestDateTimeUI.prototype.commitMonth = function () {
    var controller = this.getController();
    if (controller && typeof controller.selectMonth === 'function') {
      controller.selectMonth(this.viewMonth, this.viewYear);
    }
  };

  EyeTestDateTimeUI.prototype.shiftMonth = function (delta) {
    var month = this.viewMonth + delta;
    var year = this.viewYear;

    if (month < 1) {
      month = 12;
      year -= 1;
    } else if (month > 12) {
      month = 1;
      year += 1;
    }

    var today = startOfTodayLocal();
    var currentMonth = today.getMonth() + 1;
    var currentYear = today.getFullYear();
    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return;
    }

    this.viewMonth = month;
    this.viewYear = year;
    this.commitMonth();
    this.renderCalendar();
    this.renderTimeSlots();
    this.syncConfirmEnabled();
    this.announce(
      formatMonthTitle(this.viewYear, this.viewMonth, this.labels)
    );
  };

  EyeTestDateTimeUI.prototype.selectDate = function (isoDate) {
    var date = String(isoDate || '').trim();
    if (!date || !this.availableDateSet[date]) return;

    this.selectedDate = date;
    this.selectedTime = null;
    this._focusedDate = date;

    var controller = this.getController();
    if (controller && typeof controller.selectDate === 'function') {
      controller.selectDate(date);
    }

    this.syncDaySelectionUI();
    this.setDayTabStops(date);
    this.renderTimeSlots();
    this.syncConfirmEnabled();
    this.announce(date + ' selected.');
  };

  EyeTestDateTimeUI.prototype.selectTime = function (time) {
    var value = String(time || '').trim();
    if (!value || !this.selectedDate) return;

    this.selectedTime = value;

    var controller = this.getController();
    if (controller && typeof controller.selectTime === 'function') {
      controller.selectTime(value);
    }

    this.syncSlotSelectionUI();
    this.setSlotTabStops(value);
    this.syncConfirmEnabled();
    this.announce(value + ' selected.');
  };

  EyeTestDateTimeUI.prototype.syncConfirmEnabled = function () {
    var btn = this.cache.confirmBtn;
    if (!btn) return;
    var enabled = !!(this.selectedDate && this.selectedTime);
    btn.disabled = !enabled;
    btn.setAttribute('aria-disabled', enabled ? 'false' : 'true');
  };

  EyeTestDateTimeUI.prototype.announce = function (message) {
    if (!this.cache.a11y || !message) return;
    this.cache.a11y.textContent = message;
  };

  EyeTestDateTimeUI.prototype.syncMonthNav = function () {
    var today = startOfTodayLocal();
    var currentMonth = today.getMonth() + 1;
    var currentYear = today.getFullYear();
    var atCurrent =
      this.viewYear < currentYear ||
      (this.viewYear === currentYear && this.viewMonth <= currentMonth);

    if (this.cache.prevBtn) {
      this.cache.prevBtn.disabled = atCurrent;
      this.cache.prevBtn.setAttribute('aria-disabled', atCurrent ? 'true' : 'false');
    }
  };

  EyeTestDateTimeUI.prototype.createDayButton = function (model) {
    var template = this.cache.dayTemplate;
    if (!template || !template.content) return null;

    var fragment = template.content.cloneNode(true);
    var btn = fragment.querySelector('[data-basari-eye-test-day]');
    if (!btn) return null;

    var label = btn.querySelector('[data-basari-eye-test-day-label]');
    if (label) label.textContent = String(model.day);

    btn.setAttribute('data-date', model.iso || '');
    btn.setAttribute('aria-label', model.ariaLabel || String(model.day));

    btn.classList.toggle(OUTSIDE_CLASS, !!model.outside);
    btn.classList.toggle(PAST_CLASS, !!model.past);
    btn.classList.toggle(TODAY_CLASS, !!model.today);
    btn.classList.toggle(DISABLED_CLASS, !!model.disabled);
    btn.classList.toggle(SELECTED_CLASS, !!model.selected);

    if (model.disabled) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('tabindex', '-1');
    } else {
      btn.disabled = false;
      btn.setAttribute('aria-disabled', 'false');
      btn.setAttribute('tabindex', '-1');
    }

    if (model.selected) {
      btn.setAttribute('aria-selected', 'true');
      btn.setAttribute('aria-current', 'date');
    } else {
      btn.setAttribute('aria-selected', 'false');
      btn.removeAttribute('aria-current');
    }

    return btn;
  };

  EyeTestDateTimeUI.prototype.buildCalendarModels = function () {
    var year = this.viewYear;
    var month = this.viewMonth;
    var today = startOfTodayLocal();
    var firstWeekday = mondayIndex(year, month, 1);
    var totalDays = daysInMonth(year, month);
    var prevMonth = month === 1 ? 12 : month - 1;
    var prevYear = month === 1 ? year - 1 : year;
    var prevTotal = daysInMonth(prevYear, prevMonth);
    var models = [];
    var i;
    var day;
    var iso;
    var dateObj;
    var outside;
    var past;
    var available;
    var disabled;

    /* Leading overflow from previous month */
    for (i = firstWeekday - 1; i >= 0; i -= 1) {
      day = prevTotal - i;
      iso = toISODate(prevYear, prevMonth, day);
      models.push({
        day: day,
        iso: iso,
        outside: true,
        past: true,
        today: false,
        disabled: true,
        selected: false,
        ariaLabel: iso
      });
    }

    for (day = 1; day <= totalDays; day += 1) {
      iso = toISODate(year, month, day);
      dateObj = new Date(year, month - 1, day);
      past = dateObj < today;
      available = !!this.availableDateSet[iso];
      disabled = past || !available;
      models.push({
        day: day,
        iso: iso,
        outside: false,
        past: past,
        today:
          dateObj.getFullYear() === today.getFullYear() &&
          dateObj.getMonth() === today.getMonth() &&
          dateObj.getDate() === today.getDate(),
        disabled: disabled,
        selected: this.selectedDate === iso,
        ariaLabel: iso
      });
    }

    /* Trailing overflow to complete the final week only */
    var nextMonth = month === 12 ? 1 : month + 1;
    var nextYear = month === 12 ? year + 1 : year;
    var trailing = (7 - (models.length % 7)) % 7;

    for (i = 1; i <= trailing; i += 1) {
      iso = toISODate(nextYear, nextMonth, i);
      models.push({
        day: i,
        iso: iso,
        outside: true,
        past: false,
        today: false,
        disabled: true,
        selected: false,
        ariaLabel: iso
      });
    }

    return models;
  };

  EyeTestDateTimeUI.prototype.renderCalendar = function () {
    if (!this.cache.grid) return;

    var monthData = this.availabilityService.getAvailableDates(this.viewYear, this.viewMonth);
    var i;
    this.availableDateSet = {};
    for (i = 0; i < monthData.availableDates.length; i += 1) {
      this.availableDateSet[monthData.availableDates[i]] = true;
    }

    if (this.cache.monthTitle) {
      this.cache.monthTitle.textContent = formatMonthTitle(
        this.viewYear,
        this.viewMonth,
        this.labels
      );
    }

    this.syncMonthNav();

    var models = this.buildCalendarModels();
    this.cache.grid.innerHTML = '';

    var btn;
    for (i = 0; i < models.length; i += 1) {
      btn = this.createDayButton(models[i]);
      if (btn) this.cache.grid.appendChild(btn);
    }

    this.setDayTabStops(this.selectedDate || this._focusedDate);
  };

  EyeTestDateTimeUI.prototype.syncDaySelectionUI = function () {
    var buttons = this.cache.grid
      ? Array.prototype.slice.call(this.cache.grid.querySelectorAll('[data-basari-eye-test-day]'))
      : [];
    var selected = String(this.selectedDate || '');
    var i;
    var btn;
    var isSelected;

    for (i = 0; i < buttons.length; i += 1) {
      btn = buttons[i];
      isSelected = btn.getAttribute('data-date') === selected && !btn.disabled;
      btn.classList.toggle(SELECTED_CLASS, isSelected);
      btn.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      if (isSelected) {
        btn.setAttribute('aria-current', 'date');
      } else {
        btn.removeAttribute('aria-current');
      }
    }
  };

  EyeTestDateTimeUI.prototype.createSlotButton = function (slot) {
    var template = this.cache.slotTemplate;
    if (!template || !template.content) return null;

    var fragment = template.content.cloneNode(true);
    var btn = fragment.querySelector('[data-basari-eye-test-slot]');
    if (!btn) return null;

    var timeEl = btn.querySelector('[data-basari-eye-test-slot-time]');
    if (timeEl) timeEl.textContent = slot.time;

    btn.setAttribute('data-time', slot.time);
    btn.setAttribute('aria-label', slot.time);

    var available = slot.available !== false;
    btn.classList.toggle(DISABLED_CLASS, !available);
    btn.classList.toggle(SELECTED_CLASS, false);

    if (!available) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('tabindex', '-1');
      btn.setAttribute('aria-selected', 'false');
    } else {
      btn.disabled = false;
      btn.setAttribute('aria-disabled', 'false');
      btn.setAttribute('tabindex', '-1');
      btn.setAttribute('aria-selected', 'false');
    }

    return btn;
  };

  EyeTestDateTimeUI.prototype.setSlotTabStops = function (focusTime) {
    var buttons = this.getSlotButtons();
    var target = focusTime || this.selectedTime;
    var i;
    var matched = false;

    for (i = 0; i < buttons.length; i += 1) {
      if (!matched && target && buttons[i].getAttribute('data-time') === target) {
        buttons[i].setAttribute('tabindex', '0');
        matched = true;
      } else {
        buttons[i].setAttribute('tabindex', '-1');
      }
    }

    if (!matched && buttons.length) {
      buttons[0].setAttribute('tabindex', '0');
    }
  };

  EyeTestDateTimeUI.prototype.renderTimeSlots = function () {
    var i;
    var period;
    var list;
    var slots;
    var slotData;
    var btn;
    var hasAny = false;

    if (!this.selectedDate || !this.availableDateSet[this.selectedDate]) {
      for (i = 0; i < PERIODS.length; i += 1) {
        list = this.cache.slotLists[PERIODS[i]];
        if (list) list.innerHTML = '';
      }
      setHidden(this.cache.slotsEmpty, false);
      setHidden(this.cache.slotGroups, true);
      return;
    }

    slotData = this.availabilityService.getAvailableSlots(this.selectedDate);

    for (i = 0; i < PERIODS.length; i += 1) {
      period = PERIODS[i];
      list = this.cache.slotLists[period];
      if (!list) continue;
      list.innerHTML = '';
      slots = slotData[period] || [];
      var j;
      for (j = 0; j < slots.length; j += 1) {
        btn = this.createSlotButton(slots[j]);
        if (btn) {
          list.appendChild(btn);
          if (slots[j].available !== false) hasAny = true;
        }
      }
    }

    setHidden(this.cache.slotsEmpty, hasAny);
    setHidden(this.cache.slotGroups, !hasAny);
    this.syncSlotSelectionUI();
    this.setSlotTabStops(this.selectedTime);
  };

  EyeTestDateTimeUI.prototype.syncSlotSelectionUI = function () {
    var buttons = Array.prototype.slice.call(
      this.root.querySelectorAll('[data-basari-eye-test-slot]')
    );
    var selected = String(this.selectedTime || '');
    var i;
    var btn;
    var isSelected;

    for (i = 0; i < buttons.length; i += 1) {
      btn = buttons[i];
      isSelected =
        !btn.disabled &&
        btn.getAttribute('aria-disabled') !== 'true' &&
        btn.getAttribute('data-time') === selected;
      btn.classList.toggle(SELECTED_CLASS, isSelected);
      btn.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    }
  };

  EyeTestDateTimeUI.prototype.handleConfirm = function () {
    if (!this.selectedDate || !this.selectedTime) return;

    var controller = this.getController();
    if (controller && typeof controller.requestNavigation === 'function') {
      controller.requestNavigation(ROUTE_CONFIRMATION);
      return;
    }

    if (
      global.BasariEyeTestBooking &&
      global.BasariEyeTestBooking.ROUTES &&
      typeof this.options.navigate === 'function'
    ) {
      this.options.navigate(global.BasariEyeTestBooking.ROUTES.CONFIRMATION);
    }
  };

  EyeTestDateTimeUI.prototype.handleClose = function () {
    if (typeof this.options.onClose === 'function') {
      this.options.onClose();
      return;
    }

    var controller = this.getController();
    if (controller && typeof controller.closeDrawer === 'function') {
      controller.closeDrawer();
    }
  };

  EyeTestDateTimeUI.prototype.refresh = function () {
    this.hydrateFromState();
    this.commitMonth();
    this.renderCalendar();
    this.renderTimeSlots();
    this.syncConfirmEnabled();
  };

  EyeTestDateTimeUI.prototype.destroy = function () {
    this.unbind();
    if (this.root) {
      this.root.removeAttribute(READY_ATTR);
    }
    this.cache = {};
    this.selectedDate = null;
    this.selectedTime = null;
  };

  /**
   * @param {HTMLElement} root
   * @param {Object} options
   * @returns {EyeTestDateTimeUI|null}
   */
  function mount(root, options) {
    if (!root) return null;

    if (root.getAttribute(READY_ATTR) === 'true' && root._basariEyeTestDateTimeUI) {
      root._basariEyeTestDateTimeUI.refresh();
      return root._basariEyeTestDateTimeUI;
    }

    var ui = new EyeTestDateTimeUI(root, options || {});
    ui.cacheSelectors();
    ui.bind();
    ui.refresh();
    root.setAttribute(READY_ATTR, 'true');
    root._basariEyeTestDateTimeUI = ui;
    return ui;
  }

  BasariEyeTestDateTime.AvailabilityService = AvailabilityService;
  BasariEyeTestDateTime.MOCK_SLOT_CATALOG = MOCK_SLOT_CATALOG;
  BasariEyeTestDateTime.mount = mount;
  BasariEyeTestDateTime.EyeTestDateTimeUI = EyeTestDateTimeUI;

  global.BasariEyeTestDateTime = BasariEyeTestDateTime;
})(typeof window !== 'undefined' ? window : this);
